import { User, UserRole } from '@/types/user.types'

export const ROLE_LABELS: Record<UserRole, string> = {
  [UserRole.ADMIN]: 'Administrateur',
  [UserRole.VIEWER]: 'Visualiseur',
  [UserRole.RESELLER]: 'Revendeur',
  [UserRole.WHOLESALER]: 'Grossiste',
}

export const ALL_ROLES = Object.values(UserRole) as UserRole[]

export const NEO_PLATFORM_ROLES: UserRole[] = [UserRole.ADMIN, UserRole.VIEWER]

export function isAdminUser(
  user: Pick<{ role: UserRole }, 'role'> | null | undefined
): boolean {
  return user?.role === UserRole.ADMIN
}

export function isNeoPlatformRole(role: UserRole): boolean {
  return NEO_PLATFORM_ROLES.includes(role)
}

export function hasAnyRole(
  user: Pick<{ role: UserRole }, 'role'> | null | undefined,
  roles: UserRole[],
  allowAdmin: boolean = true
): boolean {
  if (!user) return false
  if (allowAdmin && isAdminUser(user)) return true
  return roles.includes(user.role)
}

export function canManageNeoUsers(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canManageResellers(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canManageWholesalers(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canAccessAttachedResellers(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return hasAnyRole(user, [UserRole.ADMIN, UserRole.WHOLESALER])
}

export function canManageAttachedResellers(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return user?.role === UserRole.WHOLESALER
}

export function canAccessClients(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return hasAnyRole(user, [UserRole.ADMIN, UserRole.VIEWER, UserRole.RESELLER])
}

export function canManageClients(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return hasAnyRole(user, [UserRole.ADMIN, UserRole.RESELLER])
}

export function canManageLicenses(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return canManageClients(user)
}

export function canExportCompositionDuParc(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return hasAnyRole(user, [UserRole.ADMIN, UserRole.VIEWER, UserRole.RESELLER])
}

export function canManageClientGroups(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canAccessAttachedClients(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return isAdminUser(user)
}

export function canAccessActivityLogs(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return hasAnyRole(user, NEO_PLATFORM_ROLES)
}

export function canAccessSettings(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return hasAnyRole(user, NEO_PLATFORM_ROLES)
}

export function canAccessFacturation(
  user: Pick<User, 'role' | 'wholesaler'> | null | undefined
): boolean {
  if (!user) return false
  if (hasAnyRole(user, NEO_PLATFORM_ROLES)) return true
  if (user.role === UserRole.WHOLESALER) return true
  return isBillableActor(user)
}

export function canEditSettings(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canAccessApkSettings(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return hasAnyRole(user, NEO_PLATFORM_ROLES)
}

export function canManageApk(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function canDownloadApk(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return hasAnyRole(user, [UserRole.RESELLER, UserRole.WHOLESALER], false)
}

export function canAccessDashboard(
  user: Pick<{ role: UserRole }, 'role'> | null
): boolean {
  return hasAnyRole(user, [
    UserRole.ADMIN,
    UserRole.VIEWER,
    UserRole.RESELLER,
    UserRole.WHOLESALER,
  ])
}

export function canAccessKpis(user: Pick<{ role: UserRole }, 'role'> | null): boolean {
  return isAdminUser(user)
}

export function isAttachedReseller(
  user: Pick<User, 'role' | 'wholesaler'> | null | undefined
): boolean {
  return user?.role === UserRole.RESELLER && Boolean(user.wholesaler)
}

export function isBillableActor(
  user: Pick<User, 'role' | 'wholesaler'> | null | undefined
): boolean {
  if (!user) return false
  if (user.role === UserRole.WHOLESALER) return true
  if (user.role === UserRole.RESELLER) return !user.wholesaler
  return false
}

export function canEditActorIdentity(
  user: Pick<User, 'role' | 'wholesaler'> | null | undefined
): boolean {
  return isBillableActor(user)
}

export function getInheritedIdentity(
  user: User | null | undefined
): Pick<User, 'companyName' | 'primaryColor' | 'secondaryColor' | 'logo' | 'fluxId'> | null {
  if (!isAttachedReseller(user) || !user?.wholesaler || typeof user.wholesaler !== 'object') {
    return null
  }

  return user.wholesaler
}
